import type {
  AlternativeQuotes,
  AlternativeQuotesDTO,
  QuoteDetailsDTO,
  QuoteEntryDTO,
  QuoteLineItem,
  QuoteLineItemDTO,
} from "~/OrdersQuotes/QuoteListPage/api/fetchOrderDetails/dto";
import type { ConfiguratorProductData, KITProductData, PlainProductData } from "~/models/ProductData";
import { getLineItemLineNo } from "~/OrdersQuotes/OrderListPage/api/fetchOrderHistory/fetchOrderHistory";
import { getDate } from "~/api/ApiHelper";
import {
  getNetPrice,
  transformOrderDetails,
} from "~/OrdersQuotes/OrderListPage/api/fetchOrderDetails/fetchOrderDetails";
import * as PriceUtils from "~/utils/PriceUtils";
import { resolveUrl } from "~/utils/url/urlResolver";
import { getItemStatus, QuoteDetailsEntryStatus } from "~/OrdersQuotes/common/GetItemStatus";
import type { DetailedQuoteData, DetailedQuoteLineItemData, QuoteLineItemData } from "~/OrdersQuotes/common/modelsV2";
import { DEFAULT_UNIT_SYMBOL } from "~/utils/constants";
import * as WeightUtils from "~/utils/WeightUtils";
import { transformProduct } from "~/ProductDetails/transformers/ProductDataTransformer";
import { constructQuoteDetailsUrl } from "~/OrdersQuotes/QuoteListPage/api/fetchQuoteHistory/fetchQuoteHistory";

export function transformQuoteDetails(data: QuoteDetailsDTO): DetailedQuoteData {
  const orderDetails = transformOrderDetails(data);

  const statusEnum = getItemStatus(data.orderStatusCode);

  const entries = data.entries ?? [];

  return {
    ...orderDetails,
    detailsUrl: constructQuoteDetailsUrl(data.soNumber, data),
    orderAttainment: data.quoteResponsibleName,
    quoteNumber: data.soNumber,
    isBundled: data.isBundled ?? false,

    statusEnum: statusEnum,

    products: entries.map(transformQuoteDetailLineItem),
    orderFullfilment: data.fulfilmentResponsibleName,
    quoteType: data.orderType,
    allowOrderEntry: data.checkoutAllowed,
    totalSelectablePrice: data.selectableItemTotalPrice || PriceUtils.zero(),
    totalSelectableWeight: WeightUtils.getWeightObject(
      data.selectableItemWeight || 0,
      data.orderWeightUnit !== null ? data.orderWeightUnit : DEFAULT_UNIT_SYMBOL
    ),
    dateExpired: data.dateExpired ? new Date(data.dateExpired) : undefined,
    quoteDocumentUrl: data.quoteDocumentUrl,
  };
}

export function transformQuoteDetailLineItem(data: QuoteEntryDTO): DetailedQuoteLineItemData {
  const quoteLineItem = transformQuoteLineItem(data);

  let messages: string[] = [];
  const itemStatus = [];
  if (data.mainQuotes) {
    itemStatus.push(getItemStatus(data.mainQuotes[0].lineItemStatusCode));
  }
  if (data.alternativeQuotes && data.alternativeQuotes.length > 0) {
    for (let i = 0; i < data.alternativeQuotes.length; i++) {
      itemStatus.push(getItemStatus(data.alternativeQuotes[i].entries[0].lineItemStatusCode));
    }
  }
  if (itemStatus.includes(QuoteDetailsEntryStatus.IN_CLARIFICATION)) {
    messages = ["page.quoteDetails.message.notAvailable2"];
  } else if (
    itemStatus.includes(QuoteDetailsEntryStatus.EXPIRED) ||
    itemStatus.includes(QuoteDetailsEntryStatus.REJECTED)
  ) {
    messages = ["page.quoteDetails.message.notAvailable"];
  }

  return <DetailedQuoteLineItemData>{
    ...quoteLineItem,
    messages,
    statusEnum: itemStatus[0],
    internalComment: data?.internalComment ?? "",
    disabled: !data.orderable && !itemStatus.includes(QuoteDetailsEntryStatus.RELEASED),
    orderable: data.orderable,
  };
}

function transformQuoteLineItem(data: QuoteEntryDTO): QuoteLineItemData {
  const mass = data.massPerPiece !== null ? data.massPerPiece : 0;
  const unit = data.massUnit !== null ? data.massUnit : DEFAULT_UNIT_SYMBOL;

  const weight = WeightUtils.getWeightObject(mass, unit);

  const product = transformProduct(data.product);

  return {
    packCode: data.packCode,
    packSize: data.packSize ?? 1,
    weight,
    product,
    location: data.primaryWarehouseName,
    priceReference: data.priceReference || "",
    priceReferenceDescription: data.priceReferenceDescription || "",
    orderable: data.orderable,
    mainQuotes: (data.mainQuotes ?? []).map((item) =>
      transformMainQuoteLine(item, product, data.orderable, data.primaryWarehouseName)
    ),
    alternativeQuotes: (data.alternativeQuotes ?? []).map((item) =>
      transformAlternativeQuoteLine(item, product, data.orderable, data.primaryWarehouseName)
    ),
    alternativeProducts: data.alternativeProducts,
  };
}

function transformAlternativeQuoteLine(
  data: AlternativeQuotesDTO,
  product: PlainProductData | ConfiguratorProductData | KITProductData,
  orderable: boolean,
  location?: string
): AlternativeQuotes {
  return {
    lineNumber: data.index,
    linesItem: data.entries.map((item) => ({
      lineNo: getLineItemLineNo(item),
      lineNumber: item.lineNumber ?? 0,
      scheduleLineNumber: item.scheduleLineNumber ?? 0,
      comment: item.comment || "",
      qty: item.quantityRequested,
      confirmedQty: item.quantityConfirmed,
      shippedQty: item.quantityShipped,
      deliveryDate: getDate(item.deliveryDate),
      requestedDeliveryDate: getDate(item.requestedDeliveryDate),
      netPrice: getNetPrice(item, product),
      priceForTotal: item.priceForTotal || PriceUtils.zero(),
      status: item.lineItemStatus,
      statusCode: item.lineItemStatusCode,
      quoteNumber: item.quoteNumber || "",
      contractNumber: item.contractNumber ?? "",
      contractLine: item.contractLine ?? "",
      orderable: item.orderable ? item.orderable : orderable,
      logisticalComment: item.logisticalComment ?? "",
      invoiceData:
        !item.shipmentInformation || !item.shipmentInformation.shippingPartnerID
          ? undefined
          : {
              trackingUrl: item.shipmentInformation.trackingUrl ? item.shipmentInformation.trackingUrl : undefined,
              invoiceDate: item.shipmentInformation.invoiceDate || "",
              type: item.shipmentInformation.shippingPartnerName || "",
              invoiceNo: item.shipmentInformation.invoiceNumber || "",
              invoiceUrl: item.shipmentInformation.invoiceUrl ? resolveUrl(item.shipmentInformation.invoiceUrl) : null,
              trackingNo: item.shipmentInformation.trackingNumber || "-",
              shippingPartnerId: item.shipmentInformation.shippingPartnerID || "",
              shippingPartnerName: item.shipmentInformation.shippingPartnerName || "",
            },
      notaFiscal:
        item.shipmentInformation && item.shipmentInformation.notaFiscal ? item.shipmentInformation.notaFiscal : null,
      product: product,
      location: location ?? "-",
      statusEnum: getItemStatus(item.lineItemStatusCode),
      disabled: item.lineItemStatusCode !== ("OV10" || "OV20"),
    })),
  };
}

function transformMainQuoteLine(
  data: QuoteLineItemDTO,
  product: PlainProductData | ConfiguratorProductData | KITProductData,
  orderable: boolean,
  location?: string
): QuoteLineItem {
  const statusEnum = getItemStatus(data.lineItemStatusCode);
  return {
    lineNo: getLineItemLineNo(data),
    lineNumber: data.lineNumber ?? 0,
    scheduleLineNumber: data.scheduleLineNumber ?? 0,
    comment: data.comment || "",
    qty: data.quantityRequested,
    confirmedQty: data.quantityConfirmed,
    shippedQty: data.quantityShipped,
    deliveryDate: getDate(data.deliveryDate),
    requestedDeliveryDate: getDate(data.requestedDeliveryDate),
    netPrice: getNetPrice(data, product),
    priceForTotal: data.priceForTotal || PriceUtils.zero(),
    status: data.lineItemStatus,
    statusCode: data.lineItemStatusCode,
    quoteNumber: data.quoteNumber || "",
    contractNumber: data.contractNumber ?? "",
    contractLine: data.contractLine ?? "",
    orderable: data.orderable ? data.orderable : orderable,
    logisticalComment: data.logisticalComment ?? "",
    product: product,
    location: location ?? "-",
    statusEnum: statusEnum,
    disabled: data.lineItemStatusCode !== ("OV10" || "OV20"),
  };
}
